import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  CharlesErrorState,
  getCharlesErrorCopy,
} from "@/components/employee/CharlesErrorStates";
import { CharlesPrivateBadge } from "@/components/employee/CharlesPrivateBadge";
import { useCharles } from "@/contexts/CharlesContext";
import { cn } from "@/lib/utils";

export default function Charles() {
  const {
    session,
    conversation,
    configError,
    started,
    forceTextOnly,
    micDenied,
    voiceOptIn,
    start,
    stop,
    sendText,
  } = useCharles();

  const [text, setText] = useState("");

  if (configError) {
    return (
      <div className="min-h-[100dvh] bg-white flex items-center justify-center p-6">
        <CharlesErrorState code="config_error" />
      </div>
    );
  }
  if (session.status === "loading") {
    return (
      <div className="min-h-[100dvh] bg-white flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (session.status === "error") {
    return (
      <div className="min-h-[100dvh] bg-white flex items-center justify-center p-6">
        <CharlesErrorState code={session.code} />
      </div>
    );
  }

  const showVoiceUI = voiceOptIn && !forceTextOnly;
  const micNotice = micDenied ? getCharlesErrorCopy("mic_denied") : null;
  const isConnected = conversation.status === "connected";
  const isSpeaking = conversation.isSpeaking;

  const statusLabel = !started
    ? showVoiceUI
      ? "Tap the sphere to start speaking"
      : "Tap the sphere to start chatting"
    : isConnected
      ? isSpeaking
        ? "Charles is speaking…"
        : "Listening"
      : "Connecting…";

  return (
    <div className="min-h-[100dvh] bg-white text-neutral-900 flex flex-col">
      {/* Minimal header */}
      <header className="px-6 pt-8 sm:pt-12 text-center space-y-2">
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">
          Talk to Charles
        </h1>
        <p className="text-sm text-neutral-500">
          A private space to think out loud.
        </p>
        <div className="flex justify-center pt-1">
          <CharlesPrivateBadge />
        </div>
      </header>

      {/* Centerpiece sphere */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 gap-10">
        <button
          onClick={() => {
            if (!started) start();
          }}
          aria-label={started ? "Charles is active" : "Start conversation with Charles"}
          className={cn(
            "relative rounded-full transition-transform duration-300",
            !started && "hover:scale-[1.03] active:scale-[0.98] cursor-pointer",
            started && "cursor-default",
          )}
          style={{
            height: "min(60vw, 280px)",
            width: "min(60vw, 280px)",
          }}
        >
          {/* Soft ambient glow — crossfade orange/green */}
          <span
            aria-hidden
            className="absolute -inset-16 rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(255,138,61,0.18) 0%, rgba(255,138,61,0) 70%)",
              opacity: isConnected ? 0 : 1,
              transition: "opacity 700ms ease",
            }}
          />
          <span
            aria-hidden
            className="absolute -inset-16 rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(52,211,153,0.28) 0%, rgba(52,211,153,0) 70%)",
              opacity: isConnected ? 1 : 0,
              animation: isConnected ? "pulse 2s ease-in-out infinite" : undefined,
              transition: "opacity 700ms ease",
            }}
          />

          {/* Speaking ripple */}
          {isSpeaking && (
            <span
              aria-hidden
              className="absolute inset-0 rounded-full animate-ping"
              style={{
                background: isConnected
                  ? "rgba(52, 211, 153, 0.4)"
                  : "rgba(255, 138, 61, 0.35)",
              }}
            />
          )}

          {/* Sphere — orange base layer (always rendered); stops in design/tokens.css */}
          <span
            aria-hidden
            className="absolute inset-0 rounded-full"
            style={{
              background:
                "radial-gradient(circle at 30% 28%, hsl(var(--orb-stop-1)) 0%, hsl(var(--orb-stop-2)) 38%, hsl(var(--orb-stop-3)) 75%, hsl(var(--orb-stop-4)) 100%)",
              boxShadow:
                "0 30px 80px -20px hsl(var(--orb-stop-3) / 0.35), inset -10px -16px 32px hsl(var(--ink) / 0.28)",
            }}
          />
          {/* Sphere — green layer, fades in over the orange */}
          <span
            aria-hidden
            className={cn(
              "absolute inset-0 rounded-full",
              isConnected && "animate-pulse",
            )}
            style={{
              background:
                "radial-gradient(circle at 30% 28%, hsl(var(--orb-stop-1-alt)) 0%, hsl(var(--orb-stop-2-alt)) 38%, hsl(var(--orb-stop-3-alt)) 75%, hsl(var(--orb-stop-4-alt)) 100%)",
              boxShadow:
                "0 30px 80px -20px hsl(var(--orb-stop-3-alt) / 0.5), 0 0 60px 10px hsl(var(--orb-stop-2-alt) / 0.45), inset -10px -16px 32px hsl(var(--ink) / 0.28)",
              opacity: isConnected ? 1 : 0,
              transition: "opacity 900ms ease, box-shadow 900ms ease",
            }}
          />


          {/* Highlight */}
          <span
            aria-hidden
            className="absolute rounded-full opacity-80"
            style={{
              top: "14%",
              left: "20%",
              height: "14%",
              width: "14%",
              background: "rgba(255,255,255,0.8)",
              filter: "blur(4px)",
            }}
          />
        </button>

        <p className="text-sm text-neutral-500 text-center min-h-[1.25rem]">
          {statusLabel}
        </p>

        {micNotice && (
          <p className="max-w-sm text-xs text-neutral-500 text-center border border-neutral-200 rounded-md px-3 py-2">
            <span className="font-medium text-neutral-900">{micNotice.title}.</span>{" "}
            {micNotice.body}
          </p>
        )}

        {started && !showVoiceUI && (
          <div className="w-full max-w-md flex gap-2">
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Say what's on your mind…"
              onKeyDown={(e) => {
                if (e.key === "Enter" && text.trim()) {
                  sendText(text);
                  setText("");
                }
              }}
            />
            <Button
              onClick={() => {
                sendText(text);
                setText("");
              }}
            >
              Send
            </Button>
          </div>
        )}
      </main>

      {/* Footer action */}
      <footer className="px-6 pb-10 sm:pb-12 flex justify-center">
        {started ? (
          <Button
            variant="ghost"
            onClick={stop}
            className="text-neutral-500 hover:text-neutral-900"
          >
            End conversation
          </Button>
        ) : (
          <div className="h-10" />
        )}
      </footer>
    </div>
  );
}
